import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Shield, Clock, AlertTriangle, XOctagon, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const STATUS_CONFIG = {
  ACTIVE: { icon: Shield, color: "text-lv-active", bg: "bg-lv-active/10 border-lv-active/20", label: "Active" },
  PENDING: { icon: Clock, color: "text-bronze", bg: "bg-bronze/10 border-bronze/20", label: "Pending" },
  SUSPENDED: { icon: AlertTriangle, color: "text-lv-inactive", bg: "bg-lv-inactive/10 border-lv-inactive/20", label: "Suspended" },
  EXPIRED: { icon: XOctagon, color: "text-lv-inactive", bg: "bg-lv-inactive/10 border-lv-inactive/20", label: "Expired" },
};

function StatusMessage({ status, name, hasCredential }) {
  if (status === "ACTIVE" && !hasCredential) return (
    <div className="text-center py-5 px-4" data-testid="pass-credential-pending">
      <Shield size={28} className="text-bronze mx-auto mb-3" />
      <p className="text-sm text-cream tracking-wide font-medium mb-1">Your Pass is being prepared</p>
      <p className="text-xs text-lv-muted mt-2 leading-relaxed max-w-[260px] mx-auto">
        Your membership is active. Please refresh in a moment to view your credential.
      </p>
    </div>
  );
  if (status === "PENDING") return (
    <div className="text-center py-5 px-4" data-testid="pass-pending">
      <Clock size={28} className="text-bronze mx-auto mb-3" />
      <p className="text-sm text-cream tracking-wide font-medium mb-1">Account confirmed</p>
      <p className="text-sm text-sand/80 leading-relaxed max-w-[260px] mx-auto">
        {name ? `Welcome, ${name}. ` : ""}Your LV Pass has been successfully claimed and is awaiting membership activation.
      </p>
      <p className="text-xs text-lv-muted mt-3 leading-relaxed max-w-[260px] mx-auto">
        There's nothing else you need to do right now. We'll let you know when your Pass is ready.
      </p>
    </div>
  );
  if (status === "SUSPENDED") return (
    <div className="text-center py-5 px-4" data-testid="pass-suspended">
      <AlertTriangle size={28} className="text-lv-inactive mx-auto mb-3" />
      <p className="text-sm text-cream tracking-wide font-medium mb-1">Your LV Pass is currently inactive</p>
      <p className="text-xs text-lv-muted mt-2 leading-relaxed max-w-[260px] mx-auto">
        Please contact Los Viejevos if you believe this is an error.
      </p>
    </div>
  );
  if (status === "EXPIRED") return (
    <div className="text-center py-5 px-4" data-testid="pass-expired">
      <XOctagon size={28} className="text-lv-inactive mx-auto mb-3" />
      <p className="text-sm text-cream tracking-wide font-medium mb-1">Your membership has expired</p>
      <p className="text-xs text-lv-muted mt-2 leading-relaxed max-w-[260px] mx-auto">
        Please contact Los Viejevos for renewal information.
      </p>
    </div>
  );
  return null;
}

export default function Pass() {
  const { data, isLoading, error } = useQuery({ queryKey: ["pass"], queryFn: () => api.get("/member/pass"), refetchOnMount: "always" });
  const { member: authMember } = useAuth();

  if (isLoading) return <div className="flex items-center justify-center py-32" data-testid="pass-loading"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>;
  if (error) return <div className="flex items-center justify-center py-32 px-6" data-testid="pass-error"><p className="text-cream text-sm text-center">{error.message}</p></div>;

  const { member, credential_token, verification_url } = data;
  const isActive = member.status === "ACTIVE" && credential_token;
  const cfg = STATUS_CONFIG[member.status] || STATUS_CONFIG.PENDING;
  const Icon = cfg.icon;
  const displayName = member.preferred_name || member.full_name;

  return (
    <div className="flex flex-col items-center py-6 sm:py-12 px-4" data-testid="pass-page">
      <div className="w-full max-w-[360px] relative overflow-hidden" style={{ background: "linear-gradient(180deg, #3B2A1E 0%, #241812 100%)", border: "1px solid #A8794A", borderRadius: "8px", boxShadow: "0 8px 32px rgba(20, 16, 12, 0.8)" }} data-testid="pass-card">
        <div className="p-6 sm:p-8 pb-5">
          <div className="flex items-start justify-between mb-6">
            <div className="min-w-0 flex-1 mr-3">
              <p className="text-[10px] tracking-[0.5em] uppercase text-lv-muted mb-1">Member</p>
              <p className="font-serif text-xl sm:text-2xl text-cream tracking-wide truncate">{displayName}</p>
            </div>
            <span className="font-serif text-xl sm:text-2xl text-bronze/25 leading-none shrink-0 tracking-wider">VP</span>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase text-lv-muted">Member ID</p>
              <p className="font-mono text-base sm:text-lg text-cream tracking-wider mt-0.5" data-testid="pass-member-code">{member.member_code}</p>
            </div>
            <div className="flex gap-4 sm:gap-6">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] tracking-[0.4em] uppercase text-lv-muted">Chapter</p>
                <p className="text-sm text-cream tracking-wide mt-0.5 truncate">{member.home_chapter_name}</p>
              </div>
              <div className="shrink-0">
                <p className="text-[10px] tracking-[0.4em] uppercase text-lv-muted">Since</p>
                <p className="text-sm text-cream tracking-wide mt-0.5">{member.member_since ? new Date(member.member_since).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "\u2014"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] tracking-[0.3em] uppercase rounded-full border ${cfg.bg} ${cfg.color}`} data-testid="pass-status-badge"><Icon size={12} /> {cfg.label}</span>
              {member.is_founding_member && <span className="inline-flex items-center px-3 py-1 text-[10px] tracking-[0.3em] uppercase rounded-full bg-bronze/10 text-bronze border border-bronze/20" data-testid="pass-founding-badge">Founding Member</span>}
            </div>
          </div>
        </div>
        <div className="mx-6 sm:mx-8 border-t border-bronze/20" />
        <div className="p-6 sm:p-8 pt-5 flex flex-col items-center">
          {isActive ? (
            <>
              <div className="bg-cream p-3 rounded" data-testid="pass-qr"><QRCodeSVG value={verification_url} size={140} bgColor="#F2E9D8" fgColor="#14100C" level="M" /></div>
              <p className="text-[9px] tracking-[0.3em] uppercase text-lv-muted mt-3">Scan to verify</p>
            </>
          ) : (
            <StatusMessage status={member.status} name={displayName} hasCredential={!!credential_token} />
          )}
        </div>
      </div>
      <p className="text-xs tracking-[0.2em] uppercase text-lv-muted/60 mt-5">{member.full_name}</p>

      {/* View My Privileges CTA for ACTIVE members */}
      {isActive && (
        <Link to="/privileges" className="lv-btn-primary inline-flex items-center gap-3 mt-8 min-h-[48px] px-8" data-testid="pass-view-privileges">
          View My Privileges <ArrowRight size={16} />
        </Link>
      )}
    </div>
  );
}
